import React, { useState } from 'react';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import PlanoSelector from '../components/checkout/PlanoSelector';
import DadosClienteForm from '../components/checkout/DadosClienteForm';
import MetodoPagamentoForm from '../components/checkout/MetodoPagamentoForm';
import PixPayment from '../components/checkout/PixPayment';
import CreditCardPayment from '../components/checkout/CreditCardPayment';
import asaasService from '../services/asaasService';
import webhookService from '../webhooks/webhookService';
import { Plano, Cliente, DadosCheckout, StatusCheckout, ResultadoPagamento } from '../types/checkout';

const CheckoutPage: React.FC = () => {
  const [status, setStatus] = useState<StatusCheckout>({
    step: 'plano',
    loading: false
  });
  
  const [cobrancaProcessada, setCobrancaProcessada] = useState<any>(null);
  const [clienteCriado, setClienteCriado] = useState<any>(null);
  const [dadosCheckout, setDadosCheckout] = useState<DadosCheckout | null>(null);
  const [resultadoPagamento, setResultadoPagamento] = useState<ResultadoPagamento | null>(null);

  const handlePlanoSelect = (plano: Plano) => {
    setDadosCheckout(prev => ({ ...prev, plano } as DadosCheckout));
    setStatus({ step: 'cliente', loading: false });
  };

  const handleClienteSubmit = (cliente: Cliente) => {
    setDadosCheckout(prev => ({ ...prev, cliente } as DadosCheckout));
    setStatus({ step: 'pagamento', loading: false });
  };

  const handleMetodoPagamentoSelect = (metodo: 'PIX' | 'CREDIT_CARD' | 'BOLETO') => {
    const dadosAtualizados = {
      ...dadosCheckout, 
      metodoPagamento: metodo,
      tipoPagamento: 'recorrente' // Assinatura mensal
    } as DadosCheckout;
    
    setDadosCheckout(dadosAtualizados);
    
    // Processar pagamento com os dados atualizados
    processarPagamentoComDados(dadosAtualizados);
  };

  const processarPagamentoComDados = async (dados: DadosCheckout) => {
    if (!dados) return;

    setStatus(prev => ({ ...prev, loading: true }));

    try {
      console.log('🔍 [CHECKOUT] Iniciando processamento do pagamento:', dados);

      // 1. Criar cliente no Asaas
      const cliente = await asaasService.criarCliente(dados.cliente);
      console.log('✅ [CHECKOUT] Cliente criado:', cliente);
      setClienteCriado(cliente);

      // 2. Criar cobrança ou assinatura
      let resultado;
      if (dados.metodoPagamento === 'CREDIT_CARD') {
        // Cartão de crédito: criar cobrança para processamento posterior
        resultado = await asaasService.criarCobranca(
          cliente.id, 
          dados.plano, 
          dados.metodoPagamento
        );
      } else if (dados.metodoPagamento === 'PIX') {
        // PIX: cobrança única (não assinatura)
        console.log('💰 [CHECKOUT] Criando cobrança PIX...');
        console.log('🔍 [CHECKOUT] Dados da cobrança:', { 
          clienteId: cliente.id, 
          plano: dados.plano, 
          metodo: dados.metodoPagamento 
        });
        resultado = await asaasService.criarCobranca(
          cliente.id, 
          dados.plano, 
          dados.metodoPagamento
        );
        console.log('✅ [CHECKOUT] Cobrança PIX criada:', resultado);
        
        // Obter QR Code PIX para a cobrança
        if (resultado.id) {
          console.log('🔍 [CHECKOUT] Obtendo QR Code PIX para cobrança:', resultado.id);
          try {
            const qrCodeData = await asaasService.obterQrCodePix(resultado.id);
            console.log('✅ [CHECKOUT] QR Code PIX obtido:', qrCodeData);
            
            // Adicionar dados do PIX ao resultado
            resultado.pixTransaction = {
              qrCode: qrCodeData.encodedImage,
              payload: qrCodeData.payload,
              expirationDate: qrCodeData.expirationDate
            };
          } catch (error) {
            console.error('❌ [CHECKOUT] Erro ao obter QR Code PIX:', error);
            // Continuar mesmo com erro no QR Code
          }
        }
      } else {
        throw new Error('Método de pagamento não suportado');
      }

      console.log('✅ [CHECKOUT] Pagamento criado:', resultado);

      // 3. Armazenar cobrança e mostrar componentes de pagamento
      setCobrancaProcessada(resultado);
      setStatus({ step: 'processamento', loading: false });

    } catch (error) {
      console.error('❌ [CHECKOUT] Erro no processamento:', error);
      setStatus({ 
        step: 'erro', 
        loading: false, 
        error: error instanceof Error ? error.message : 'Erro desconhecido' 
      });
    }
  };


  const handlePaymentSuccess = async (resultado: ResultadoPagamento) => {
    // Adicionar dados do plano e cliente ao resultado
    const resultadoCompleto = {
      ...resultado,
      plano: dadosCheckout?.plano,
      cliente: dadosCheckout?.cliente,
      // Dados para webhook
      webhookData: {
        email: dadosCheckout?.cliente?.email || '',
        nome: dadosCheckout?.cliente?.name || '',
        empresa: dadosCheckout?.cliente?.company || '',
        cnpj: dadosCheckout?.cliente?.cpfCnpj || '',
        plano: dadosCheckout?.plano?.nome || ''
      }
    };
    
    console.log('🎉 [CHECKOUT] Pagamento bem-sucedido:', resultadoCompleto);
    console.log('📋 [WEBHOOK] Dados para webhook:', resultadoCompleto.webhookData);
    
    // Validar dados do webhook
    const validacao = webhookService.validarDados(resultadoCompleto);
    if (validacao.valido) {
      console.log('✅ [WEBHOOK] Dados válidos, enviando...');
      
      // Tentar enviar para webhook (opcional - não bloqueia o fluxo)
      try {
        await webhookService.enviarDadosPagamento(resultadoCompleto);
      } catch (error) {
        console.warn('⚠️ [WEBHOOK] Falha ao enviar dados (não crítico):', error);
      }
    } else {
      console.warn('⚠️ [WEBHOOK] Dados inválidos:', validacao.erros);
    }
    
    setResultadoPagamento(resultadoCompleto);
    setStatus({ step: 'sucesso', loading: false });
  };

  const handlePaymentError = (error: string) => {
    setStatus({ step: 'erro', loading: false, error });
  };

  const handleBack = () => {
    switch (status.step) {
      case 'cliente':
        setStatus({ step: 'plano', loading: false });
        break;
      case 'pagamento':
        setStatus({ step: 'cliente', loading: false });
        break;
      case 'processamento':
        setStatus({ step: 'pagamento', loading: false });
        break;
      default:
        break;
    }
  };

  const renderStep = () => {
    switch (status.step) {
      case 'plano':
        return (
          <PlanoSelector 
            onSelect={handlePlanoSelect}
            planoSelecionado={dadosCheckout?.plano}
          />
        );

      case 'cliente':
        return (
          <DadosClienteForm 
            onSubmit={handleClienteSubmit}
            onBack={handleBack}
            dadosIniciais={dadosCheckout?.cliente}
          />
        );

      case 'pagamento':
        return (
          <MetodoPagamentoForm 
            onSelect={handleMetodoPagamentoSelect}
            onBack={handleBack}
            metodoSelecionado={dadosCheckout?.metodoPagamento}
            plano={dadosCheckout?.plano || { nome: '', preco: 0 }}
          />
        );


      case 'processamento':
        return (
          <div className="max-w-2xl mx-auto p-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Processando Pagamento
              </h2>
              <p className="text-gray-600">
                Complete o pagamento usando o método escolhido
              </p>
            </div>

            {/* Componentes de pagamento baseado no método */}
            {dadosCheckout?.metodoPagamento === 'PIX' && cobrancaProcessada && (
              <PixPayment 
                cobranca={cobrancaProcessada}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            )}

            {dadosCheckout?.metodoPagamento === 'CREDIT_CARD' && cobrancaProcessada && clienteCriado && (
              <CreditCardPayment 
                cobranca={cobrancaProcessada}
                cliente={clienteCriado}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            )}


            {/* Botão voltar */}
            <div className="mt-6 text-center">
              <button
                onClick={() => setStatus({ step: 'pagamento', loading: false })}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 inline mr-2" />
                Voltar
              </button>
            </div>
          </div>
        );

      case 'sucesso':
        return (
          <div className="max-w-md mx-auto p-6 text-center">
            <div className="flex justify-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Pagamento Realizado!
            </h2>
            
            <p className="text-gray-600 mb-6">
              {resultadoPagamento?.message || 'Seu pagamento foi processado com sucesso.'}
            </p>

            {/* Dados do Webhook */}
            {process.env.NODE_ENV === 'development' && (
              resultadoPagamento?.webhookData && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="font-semibold text-blue-900 mb-3">📋 Dados para Webhook:</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Email:</strong> {resultadoPagamento.webhookData.email}</div>
                    <div><strong>Nome:</strong> {resultadoPagamento.webhookData.nome}</div>
                    <div><strong>Empresa:</strong> {resultadoPagamento.webhookData.empresa || 'Não informada'}</div>
                    <div><strong>CNPJ:</strong> {resultadoPagamento.webhookData.cnpj}</div>
                    <div><strong>Plano:</strong> {resultadoPagamento.webhookData.plano}</div>
                  </div>
                  <div className="mt-3 p-2 bg-gray-100 rounded text-xs font-mono">
                    <div className="flex justify-between items-center mb-2">
                      <strong>Função SQL:</strong>
                      <button
                        onClick={() => {
                          const sql = webhookService.gerarSQL(resultadoPagamento);
                          navigator.clipboard.writeText(sql);
                          alert('SQL copiado para a área de transferência!');
                        }}
                        className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                      >
                        Copiar SQL
                      </button>
                    </div>
                    <div className="whitespace-pre-line">
                      {webhookService.gerarSQL(resultadoPagamento)}
                    </div>
                  </div>
                </div>
              )
            )}

            {resultadoPagamento?.qrCode && (
              <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">QR Code PIX</h3>
                <img 
                  src={`data:image/png;base64,${resultadoPagamento.qrCode}`} 
                  alt="QR Code PIX" 
                  className="w-48 h-48 mx-auto"
                />
              </div>
            )}


            <div className="space-y-3">
              <button
                onClick={() => window.location.href = '/'}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Ir para Dashboard
              </button>
              
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Nova Compra
              </button>
            </div>
          </div>
        );

      case 'erro':
        return (
          <div className="max-w-md mx-auto p-6 text-center">
            <div className="flex justify-center mb-6">
              <AlertCircle className="w-16 h-16 text-red-500" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Erro no Pagamento
            </h2>
            
            <p className="text-gray-600 mb-6">
              {status.error || 'Ocorreu um erro ao processar seu pagamento.'}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => setStatus({ step: 'plano', loading: false })}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Tentar Novamente
              </button>
              
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Voltar ao Dashboard
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                CIPA Fácil EHS - Checkout
              </h1>
            </div>
            
            {status.step !== 'plano' && status.step !== 'sucesso' && status.step !== 'erro' && (
              <button
                onClick={handleBack}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {status.step !== 'sucesso' && status.step !== 'erro' && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center py-4">
              {['plano', 'cliente', 'pagamento', 'processamento'].map((step, index) => (
                <React.Fragment key={step}>
                  <div className={`flex items-center ${
                    ['plano', 'cliente', 'pagamento', 'processamento'].indexOf(status.step) >= index
                      ? 'text-blue-600' 
                      : 'text-gray-400'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      ['plano', 'cliente', 'pagamento', 'processamento'].indexOf(status.step) >= index
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="ml-2 text-sm font-medium capitalize">
                      {step === 'plano' ? 'Plano' : 
                       step === 'cliente' ? 'Dados' :
                       step === 'pagamento' ? 'Pagamento' : 'Processando'}
                    </span>
                  </div>
                  {index < 3 && (
                    <div className={`flex-1 h-0.5 mx-4 ${
                      ['plano', 'cliente', 'pagamento', 'processamento'].indexOf(status.step) > index
                        ? 'bg-blue-600' 
                        : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="py-8">
        {renderStep()}
      </div>
    </div>
  );
};

export default CheckoutPage;
